import React from 'react';
import { Link } from 'react-router-dom';
import { flowRight } from 'lodash/fp';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import { withStyles } from '@material-ui/core/styles';

import Access from 'ui/Access';
import { MTable } from 'ui/mtable/MTable';
import EditButton from 'ui/EditButton';
import DetailsButton from 'ui/DetailsButton';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';
import { groupBy } from 'utils/collections';
import WhiteBack from '../../../WhiteBack';
import { withAppContext } from '../../../AppContext';

import { removeRoles, loadRoles } from './services';

const styles = theme => ({
    button: {
        margin: theme.spacing(),
        fontSize: '10px',
    },
    leftIcon: {
        marginRight: theme.spacing(),
        fontSize: '15px',
    },
});

const DELETE_ROLE = 'deleteRole';

class Roles extends React.Component {
    state = {
        roles: [],
        rolesLoading: true,
        selectedRoles: [],
        currentAction: null,
        actionResultName: null,
    };

    componentDidMount() {
        this.fetchRoles();
    }

    flatMembersInRoles = roles => {
        return roles.map(role => {
            const members = role.members.join(', ');
            return { ...role, members };
        });
    };

    fetchRoles = () => {
        const { companyId } = this.props;
        this.setState({
            roles: [],
            rolesLoading: true,
        });
        loadRoles(companyId)
            .then(roles => {
                this.setState({
                    roles: this.flatMembersInRoles(roles),
                    rolesLoading: false,
                });
            })
            .catch(() => {
                this.setState({
                    rolesLoading: false,
                });
            });
    };
    getAvailableActions = (selectedRoles, actions) => {
        const managedBy = Object.keys(groupBy(selectedRoles, 'managed'));
        let selectedStatuses = Object.keys(groupBy(selectedRoles, 'status'));
        selectedStatuses = selectedStatuses.concat(managedBy);
        selectedStatuses = new Set(selectedStatuses.sort());
        const availableActions = actions.filter(action => {
            const notAllowedUsersStatuses =
                action.notAllowedUsersStatuses || [];
            const some = notAllowedUsersStatuses.filter(status => {
                return selectedStatuses.has(status);
            });
            return some.length === 0;
        });
        return availableActions;
    };

    getActions = () => {
        return [
            {
                key: DELETE_ROLE,
                name: 'Delete roles',
                action: this.handleAction(DELETE_ROLE),
            },
        ];
    };

    getColumns = isEditable => {
        const { companyId } = this.props;
        return [
            {
                field: 'uuid',
                title: 'Uuid',
                hidden: true,
                notFilter: true,
                export: false,
            },
            {
                field: 'name',
                title: 'Name',
                cellStyle: {
                    minWidth: '20%',
                },
            },
            {
                field: 'description',
                title: 'Description',
                cellStyle: {
                    minWidth: '20%',
                },
            },
            {
                field: 'members',
                title: 'Members',
                cellStyle: {
                    minWidth: '40%',
                },
            },
            {
                field: 'action',
                title: '',
                notFilter: true,
                cellStyle: {
                    width: '150px',
                },
                export: false,
                render: row => {
                    const { uuid } = row;
                    return row.default || !isEditable ? (
                        <DetailsButton
                            component={Link}
                            to={`/companies/${companyId}/partner/roles/view/${uuid}`}
                        />
                    ) : (
                        <EditButton
                            component={Link}
                            to={`/companies/${companyId}/partner/roles/edit/${uuid}`}
                        />
                    );
                },
            },
        ];
    };

    handleAction = currentAction => selectedRoles => {
        if (currentAction === DELETE_ROLE) {
        }
        this.setState({
            currentAction,
            selectedRoles,
        });
    };

    handleCloseDialog = () => () => {
        this.setState({ currentAction: null });
    };

    handleOkDialog = ({ callback, actionResultName }) => () => {
        this.setState({ currentAction: null, actionResultName }, () =>
            callback(this.state.selectedRoles, Boolean(actionResultName))
        );
    };

    getAddRolesActions = () => (
        <Access.Change permission={Access.COMPANIES_PARTNER_ROLES}>
            <MenuList>
                <MenuItem
                    component={Link}
                    to={`/companies/${this.props.companyId}/partner/roles/new`}
                >
                    Add role
                </MenuItem>
            </MenuList>
        </Access.Change>
    );

    handleRemove = roles => {
        const { companyId } = this.props;
        removeRoles(roles, companyId).then(() => this.fetchRoles());
    };

    renderDialog = () => {
        const { currentAction } = this.state;
        const dialog = {
            [DELETE_ROLE]: {
                title: 'Delete roles',
                warning: 'Are you sure you want to delete roles:',
            },
        };

        const callbacks = {
            [DELETE_ROLE]: { callback: this.handleRemove },
        };

        if (dialog[currentAction]) {
            return (
                <ConfirmationDialog
                    open={true}
                    {...dialog[currentAction]}
                    data={this.state.selectedRoles.map(item => item.name)}
                    onCancel={this.handleCloseDialog(currentAction)}
                    onOk={this.handleOkDialog(callbacks[currentAction])}
                />
            );
        }
    };

    getActionMenuItem = selectedRoles => {
        const actions = this.getActions(this.props);
        const availableActions = this.getAvailableActions(
            selectedRoles,
            actions
        );

        const menuItems =
            availableActions.length > 0
                ? availableActions.map(action => ({
                      key: action.key,
                      name: action.name,
                      onClick: dropdownCloseCallback => {
                          action.action(selectedRoles);
                          dropdownCloseCallback();
                      },
                  }))
                : [
                      {
                          key: 'no',
                          name: 'No common action',
                          onClick: dropdownCloseCallback => {
                              dropdownCloseCallback();
                          },
                      },
                  ];

        return menuItems;
    };

    handleSelectionChange = val => {
        this.setState({ selectedRoles: val });
    };

    render() {
        const { roles, rolesLoading, selectedRoles } = this.state;
        const { app } = this.props;
        const isEditable = app.canChange(Access.COMPANIES_PARTNER_ROLES);
        const columns = this.getColumns(isEditable);

        const selectedActions = this.getActionMenuItem(selectedRoles);

        return (
            <>
                {this.state.currentAction && this.renderDialog()}
                <WhiteBack />
                <MTable
                    stateKey="Roles"
                    columns={columns}
                    data={roles}
                    loading={rolesLoading}
                    title={null}
                    actionMenuItems={selectedActions}
                    onSelectionChange={this.handleSelectionChange}
                    addMenuItems={this.getAddRolesActions}
                    canChange={isEditable}
                    options={{
                        selection: isEditable,
                        selectionProps: row => ({
                            disabled: !(isEditable && !row.default),
                        }),
                    }}
                />
            </>
        );
    }
}

export default flowRight(withStyles(styles), withAppContext)(Roles);
